import { useCallback, useEffect, useState } from 'react'

import { XIcon } from 'lucide-react'
import type { ChangeEvent, FC } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import useDebounce from '../hooks/useDebounce'
import type { OperatorAndValue } from '../TableFilters'

interface Props {
	value: OperatorAndValue
	onChange: (searchChange: OperatorAndValue) => void
	operator?: '$regex' | '$search' | '$eq'
}

const SearchInput: FC<Props> = ({ value, onChange, operator = '$regex' }) => {
	const [_value, setValue] = useState(value ? value[operator] : '')
	const debouncedValue = useDebounce(_value, 500)

	const _onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		setValue(event.target.value)
	}, [])

	const onReset = useCallback(() => {
		setValue('')
	}, [])

	useEffect(() => {
		onChange(
			debouncedValue
				? ({ [operator]: debouncedValue } as OperatorAndValue)
				: null
		)
	}, [debouncedValue])

	return (
		<div className="relative w-full max-w-sm">
			<Input
				placeholder="Search..."
				value={_value ?? ''}
				onChange={_onChange}
				className="max-w-sm max-h-8"
			/>
			{_value !== '' && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
					onClick={onReset}
				>
					<XIcon className="h-4 w-4" />
					<span className="sr-only">Clear</span>
				</Button>
			)}
		</div>
	)
}

export default SearchInput
